import { Gauge, Sparkles, FlaskConical, Hand, Package, BookOpen, Target, Leaf, Landmark, Handshake, ShoppingBag, Truck, Users } from "lucide-react";
import { useContent } from "@/content";
import { Reveal } from "@/components/Motion";
import { V3Section, V3Eyebrow, V3Title } from "@/sections/v3/shared";

const CRITERIA_ICONS = { Gauge, Sparkles, FlaskConical, Hand, Package, BookOpen, Target, Leaf };
const ECO_ICONS = { Landmark, Handshake, ShoppingBag, Truck, Users };

// 9. Critères — grille 4×2 de tuiles carrées à bordure fine.
export const Criteria3 = () => {
  const cr = useContent().criteria;
  return (
    <V3Section id={cr.id} bg="#0B0A0A" className="py-24 md:py-32">
      <V3Eyebrow>{cr.eyebrow}</V3Eyebrow>
      <V3Title testid="criteria-title">{cr.title}</V3Title>
      <Reveal delay={0.1}>
        <p className="mt-6 max-w-2xl text-[0.95rem] leading-relaxed text-white/75">{cr.intro}</p>
      </Reveal>

      <div className="mt-14 grid auto-rows-fr sm:grid-cols-2 lg:grid-cols-4" data-testid="criteria-grid">
        {cr.items.map((it, i) => {
          const Icon = CRITERIA_ICONS[it.icon] || Sparkles;
          return (
            <Reveal key={it.title} delay={(i % 4) * 0.06}>
              <div
                data-testid={`criteria-tile-${i + 1}`}
                className="group flex min-h-[270px] flex-col border border-copper/25 p-6 transition-colors duration-500 hover:bg-[#7A0F1A] md:p-7"
              >
                <Icon size={34} strokeWidth={1} className="text-copper transition-colors duration-500 group-hover:text-blush" aria-hidden="true" />
                <span className="mt-6 font-sans text-[0.75rem] tabular-nums tracking-[0.2em] text-copper/80">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-2 text-[0.82rem] uppercase leading-relaxed tracking-[0.16em] text-white">{it.title}</h3>
                <p className="mt-3 text-[0.95rem] leading-relaxed text-white/70">{it.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal className="mt-10 max-w-3xl">
        <p className="text-[0.95rem] leading-relaxed text-white/70">{cr.nb}</p>
      </Reveal>
    </V3Section>
  );
};

// 11. Écosystème — 5 colonnes égales (la 1re plus large) séparées par des filets.
export const Ecosystem3 = () => {
  const e = useContent().ecosystem;
  return (
    <V3Section id={e.id} bg="#0B0A0A" className="py-24 md:py-32">
      <V3Eyebrow>{e.eyebrow}</V3Eyebrow>
      <V3Title testid="ecosystem-title" className="max-w-2xl">{e.title}</V3Title>
      <Reveal delay={0.1}>
        <p className="mt-6 max-w-2xl text-[0.95rem] leading-relaxed text-white/75">{e.intro}</p>
      </Reveal>

      <div className="mt-14 grid gap-y-12 sm:grid-cols-2 lg:grid-cols-[1.35fr_1fr_1fr_1fr_1fr] lg:gap-y-0" data-testid="ecosystem-grid">
        {e.items.map((it, i) => {
          const Icon = ECO_ICONS[it.icon] || Landmark;
          return (
            <Reveal key={it.title} delay={i * 0.07} className={`lg:border-s lg:border-copper/25 lg:ps-8 ${i === 0 ? "lg:border-s-0 lg:ps-0 lg:pe-10" : ""}`}>
              <Icon size={30} strokeWidth={1} className="text-copper" aria-hidden="true" />
              <h3 className="mt-5 text-[0.82rem] uppercase leading-relaxed tracking-[0.18em] text-white">{it.title}</h3>
              <p className="mt-3 text-[0.95rem] leading-relaxed text-white/70">{it.text}</p>
            </Reveal>
          );
        })}
      </div>
      <Reveal delay={0.15}>
        <p className="mt-16 max-w-3xl font-serif text-[1.25rem] italic leading-snug text-blush md:text-[1.45rem]">{e.outro}</p>
      </Reveal>
    </V3Section>
  );
};
